/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2026-01-29 11:03:55
 */
import React, { useMemo, useEffect, useRef } from 'react'
import { Divider } from 'antd'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import sensors, { sensorsRole } from '../../../utils/sensors'
import CONFIG from '../../../constants/config'
import dayjs from 'dayjs'

const NewHeaderStatistics = () => {
  const dispatch = useAppDispatch()
  const myRole = useAppSelector((state) => state.group.myRole)
  const viewPerm = useAppSelector((state) => state.group.viewPerm)
  const groupID = useAppSelector((state) => state.group.groupId)
  const statistics = useAppSelector((state) => state.group.statistics)
  const isStatisticsTracked = useRef(false)

  const photosLabel = useMemo(() => {
    if (myRole > CONFIG.memberRoleTypes.NORMAL) {
      return '团队照片'
    } else if (viewPerm === CONFIG.viewPermissionTypes.GROUP) {
      return '团队照片'
    } else if (viewPerm === CONFIG.viewPermissionTypes.PERSONAL) {
      return '个人照片'
    } else if (viewPerm === CONFIG.viewPermissionTypes.DEPARTMENT) {
      return '部门照片'
    }
  }, [myRole, viewPerm])

  const photoNum = useMemo(() => {
    if (statistics) {
      return {
        limitPhotoNum:
          statistics?.limitPhotoNum < 10000
            ? statistics?.limitPhotoNum
            : statistics?.limitPhotoNum / 10000,
        limitPhotoNumUnit: statistics?.limitPhotoNum < 10000 ? '张' : '万张',
        totalNum:
          statistics?.totalNum < 10000
            ? statistics?.totalNum
            : (statistics?.totalNum / 10000).toFixed(2),
        unit: statistics?.totalNum < 10000 ? '张' : '万'
      }
    }
    return null
  }, [statistics])

  const memberCount = useMemo(() => {
    if (statistics) {
      return {
        memberCount:
          statistics?.memberCount < 10000
            ? statistics?.memberCount
            : (statistics?.memberCount / 10000).toFixed(1),
        unit: statistics?.memberCount < 10000 ? '人' : '万人'
      }
    }
    return null
  }, [statistics])

  const serverDuration = useMemo(() => {
    if (statistics) {
      const duration = dayjs.duration(statistics?.groupTime, 'seconds')
      return {
        serverDuration:
          duration.asDays() < 365
            ? Math.round(duration.asDays())
            : duration.asYears().toFixed(1),
        unit: duration.asDays() < 365 ? '天' : '年'
      }
    }
    return null
  }, [statistics])

  const storageStatus = useMemo(() => {
    if (!statistics) return null
    const limit = Number(statistics.limitPhotoNum) || 0
    const total = Number(statistics.totalNum) || 0
    const ratio = limit === 0 ? 0 : total / limit

    if (ratio >= 1) {
      return {
        text: '已满',
        style: {
          background: '#FE5049',
          color: '#ffffff'
        }
      }
    } else if (ratio >= 0.9) {
      return {
        text: '将满',
        style: {
          background: '#FFDCA5',
          color: '#6b3a00'
        }
      }
    }

    return null
  }, [statistics])

  useEffect(() => {
    if (!groupID) return
  }, [groupID])

  useEffect(() => {
    //等统计信息和团队信息都获取到了再上报
    if (statistics && myRole > -1 && !isStatisticsTracked.current) {
      const stat = statistics.data ? statistics.data : statistics
      const dayUsed = Math.round(
        dayjs.duration(stat.groupTime, 'seconds').asDays()
      )
      const money = Math.round(stat.memberCount * dayUsed * 0.8)
      sensors.track('web_workgroup_album_summary', {
        totalNum: stat.totalNum,
        fileSize: Math.round(stat.filesize / 1024 / 1024),
        memberNum: stat.memberCount,
        dayUsed: dayUsed,
        costSaving: money,
        role: sensorsRole(myRole)
      })
      isStatisticsTracked.current = true
    }
  }, [statistics, myRole, dispatch])

  return (
    <div className="header-statistics new-statistics">
      <div
        className="statistics-item"
        style={
          photoNum?.limitPhotoNum === -1
            ? { width: '75px' }
            : { width: '110px' }
        }
      >
        <div className="label-container">
          <p className="label">{photosLabel}</p>
          {storageStatus && (
            <span
              className="storage-badge"
              style={{
                ...storageStatus.style
              }}
            >
              {storageStatus.text}
            </span>
          )}
        </div>
        <p className="value">
          {photoNum ? (
            <>
              <span>{photoNum.totalNum}</span>
              {photoNum.unit}
              {photoNum.limitPhotoNum !== -1 && (
                <>
                  /{photoNum.limitPhotoNum}
                  {photoNum.limitPhotoNumUnit}
                </>
              )}
            </>
          ) : (
            '· · ·'
          )}
        </p>
      </div>
      <Divider
        orientation="vertical"
        style={{
          margin: 0,
          height: '34px',
          background: 'rgba(255,255,255, 0.2)'
        }}
      />
      <div className="statistics-item" style={{ width: '70px' }}>
        <p className="label">团队人数</p>
        <p className="value">
          {memberCount ? (
            <>
              <span>{memberCount.memberCount}</span>
              {memberCount.unit}
            </>
          ) : (
            '· · ·'
          )}
        </p>
      </div>
      <Divider
        orientation="vertical"
        style={{
          margin: 0,
          height: '34px',
          background: 'rgba(255,255,255, 0.2)'
        }}
      />
      <div className="statistics-item" style={{ width: '72px' }}>
        <p className="label">服务时长</p>
        <p className="value">
          {serverDuration ? (
            <>
              <span>{serverDuration.serverDuration}</span>
              {serverDuration.unit}
            </>
          ) : (
            '· · ·'
          )}
        </p>
      </div>
    </div>
  )
}

export default NewHeaderStatistics
